import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { IBreadCrumbEntry } from '@datachain/ui-sdk/components/breadcrumb';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import { OptionChangedEvent } from 'devextreme/ui/accordion';
import {
	CellHoverChangedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import { OrderedSet } from 'immutable';
import {
	BehaviorSubject,
	debounceTime,
	delay,
	filter,
	firstValueFrom,
	map,
	merge,
	Observable,
	of,
	ReplaySubject,
	switchMap,
	takeUntil,
	tap,
	timer,
} from 'rxjs';
import {
	checkStringEquality,
	DcDxGrids,
	DEFAULT_PAGE_SIZE,
	DEFAULT_PAGE_SIZES,
	DxFilterElem,
	DxHeaderFilterItem,
	DxOperations,
	HeaderFilterDefaultMapper,
	NoopValue,
} from 'src/app/core/dx-types';
import { GdIcons } from 'src/app/ui/app-gd.icons';

import {
	DxListsStorage,
	IDxList,
} from '../../../core/services/dx-lists.storage';
import { goToGdHome, goToProjects } from '../../../core/store';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	bulkDeleteFail,
	bulkDeleteSuccess,
	constructDatablockList,
	DatablockSelector,
	deleteDatablockFail,
	deleteDatablockSuccess,
	navigateToCreateFromDatablock,
	navigateToDatablock,
	navigateToDatablockCreate,
	navigateToDatablockDuplicate,
	navigateToExportDatablock,
	refreshDatablockList,
	stopPersistence,
	toggleDatablockStatus,
	updateDatablocksExpositionsCount,
} from '../../store';
import {
	DatablockAction,
	DatablockActionPopupComponent,
	IDatablockDeleteModalData,
} from '../datablock-action-popup/datablock-action-popup.component';
import { DatablockItemEntity, PersistenceState } from './datablock-item.entity';
import { DatablockListEntity } from './datablock-list.entity';

type OptionChangeAction = 'page-change' | 'pageSize-change' | 'sort' | 'filter';

type DatablockListOverlayTypes = {
	persistence: number | undefined;
	exposition: number | undefined;
};

type RowAction = {
	description: string;
	actionName: string;
	icon: string;
	action: (...args: Array<unknown>) => void;
};

@Component({
	selector: 'app-datablock-list',
	templateUrl: './datablock-list.component.html',
	styleUrls: ['./datablock-list.component.scss'],
	inputs: ['projectLabel'],
})
export class DatablockListComponent extends DcBaseComponent implements OnInit {
	protected readonly GdIcons = GdIcons;
	protected readonly DcIcons = DcIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly pageSize = DEFAULT_PAGE_SIZE;
	protected readonly allowPageSizes = DEFAULT_PAGE_SIZES;

	public paths: Array<IBreadCrumbEntry> = [];
	public ActionIcons = ActionIcons;
	public DxOperations = DxOperations;
	public panelActions: Array<PanelAction> = [];
	public currentlyHovered = -1;
	public toggleActions = false;
	public selectionCount = 0;
	public openedOverlayElement: DatablockListOverlayTypes = {
		persistence: -1,
		exposition: -1,
	};

	public readonly disabledRowActions: Array<RowAction> = [
		{
			actionName: 'duplicate',
			description: $localize`:i18n=@@datablock.list.action.duplicate:`,
			action: (item: unknown): void => {
				const datablock = item as DatablockItemEntity;
				this.duplicateDatablock(datablock.id);
			},
			icon: ActionIcons.DuplicateElement,
		},
		{
			actionName: 'activate',
			description: $localize`:i18n=@@general.action.activate:`,
			action: (item: unknown): void => {
				const datablock = item as DatablockItemEntity;
				this.toggleActivationStatus(datablock);
			},
			icon: ActionIcons.ActivateElement,
		},
	];
	public readonly activeRowActions: Array<RowAction> = [
		{
			actionName: 'duplicate',
			description: $localize`:i18n=@@datablock.list.action.duplicate:`,
			action: (item: unknown): void => {
				const datablock = item as DatablockItemEntity;
				this.duplicateDatablock(datablock.id);
			},
			icon: ActionIcons.DuplicateElement,
		},
		{
			actionName: 'deactivate',
			description: $localize`:i18n=@@general.action.deactivate:`,
			action: (item: unknown): void => {
				const datablock = item as DatablockItemEntity;
				this.toggleActivationStatus(datablock);
			},
			icon: ActionIcons.DeactivateElement,
		},
	];

	public tagsHeaderFilter: Array<DxHeaderFilterItem> = [];
	public modifiedByHeaderFilter: Array<DxHeaderFilterItem> = [];
	public actionByHeaderFilter: Array<DxHeaderFilterItem> = [];

	public isPersistedHeaderFilter = [
		{
			text: $localize`:i18n=@@datablock.list.column.persistance.persisted:`,
			value: true,
		},
		{
			text: $localize`:i18n=@@datablock.list.column.persistance.unpersisted:`,
			value: false,
		},
	];

	public actifHeaderFilter = [
		{
			text: $localize`:i18n=@@general.column.actif:`,
			value: ['isActive', '=', true],
		},
		{
			text: $localize`:i18n=@@general.column.unactif:`,
			value: ['isActive', '=', false],
		},
	];

	public vo$: Observable<{
		isFirstLoad: boolean;
		isLoading: boolean;
		list: DatablockListEntity;
		isLoadingPersistence: boolean;
	}>;

	public loadState = (): IDxList | undefined =>
		this.dxStorage.getListState(this.dxLocalStorageKey);

	public saveState = (state: IDxList): void => {
		this.dxStorage.setListState(this.dxLocalStorageKey, {
			...state,
			searchText: '',
		});
	};

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;
	private readonly datablockListSubject =
		new ReplaySubject<DatablockListEntity>(1);
	private readonly optionChangedSubject = new ReplaySubject<OptionChangeAction>(
		1
	);
	private readonly refreshSubject = new ReplaySubject<void>(1);

	private readonly loadTriggerSubject = new BehaviorSubject<boolean>(true);

	protected readonly ComponentIcons = ComponentIcons;
	public constructor(
		private readonly store: Store,
		private readonly datablockSelector: DatablockSelector,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons,
		private readonly dxStorage: DxListsStorage,
		private readonly modalService: AppModalService,
		private readonly actions$: Actions
	) {
		super();
		this.cmpId = 'datablock-list';
		this.dxLocalStorageKey = DcDxGrids.DatablockListStandalone;

		this.vo$ = combineAll({
			isFirstLoad: this.loadTriggerSubject,
			isLoading: this.datablockSelector.getLoading$(),
			list: this.datablockListSubject,
			isLoadingPersistence: this.datablockSelector.getLoadingPersistence$(),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.store.dispatch(constructDatablockList());

		this.handleSubscriptions();

		combineAll({
			projectLabel: this.toObservable<string>('projectLabel'),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap(({ projectLabel }) => {
					this.paths = [
						{
							label: $localize`:i18n=@@general.projects:`,
							isActive: true,
							callback: (): void => this.store.dispatch(goToProjects()),
						},
						{
							label: projectLabel,
							isActive: true,
							callback: (): void => this.store.dispatch(goToGdHome()),
						},
						{
							label: $localize`:i18n=@@datablock.list.title:`,
							isActive: false,
							callback: (): void => {},
						},
					];
				})
			)
			.subscribe();

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();

		this.datablockSelector
			.getDatablockList$()
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(320),
				filter((l) => l.availableColumns.size !== 0),
				tap((l) => {
					this.tagsHeaderFilter = l
						.extractUniqTags(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.modifiedByHeaderFilter = l
						.extractModifiers(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.actionByHeaderFilter = l
						.extractActionCreators(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();

					this.datablockListSubject.next(l);
					if (this.grid) {
						this.grid.selectedRowKeys = [];
					}
				})
			)
			.subscribe();
		this.datablockSelector
			.getLoading$()
			.pipe(
				tap((isLoading) =>
					isLoading
						? this.grid?.instance.beginCustomLoading('')
						: this.grid?.instance.endCustomLoading()
				)
			)
			.subscribe();

		// Triggers loading after importing project for the first time
		this.datablockListSubject
			.pipe(
				takeUntil(this.optionChangedSubject),
				switchMap((list) =>
					combineAll({
						list,
						count: timer(2000),
					})
				),
				tap(({ list }) => {
					this.refreshSubject.next();
				})
			)
			.subscribe();

		merge(
			this.optionChangedSubject,
			this.refreshSubject.pipe(
				delay(550),
				map(() => 'refresh')
			)
		)
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(520),
				filter(() => this.grid !== null),
				tap((val) => {
					const items = this.grid?.instance
						?.getDataSource()
						?.items() as Array<DatablockItemEntity>;
					const datablocksIds = items?.map((it) => it.id) ?? [];
					this.store.dispatch(
						updateDatablocksExpositionsCount({
							ids: datablocksIds,
							shouldReload: val === 'refresh',
						})
					);
				})
			)
			.subscribe();
	}

	public refresh(): void {
		this.loadTriggerSubject.next(true);
		this.grid?.instance.beginCustomLoading('');
		this.store.dispatch(refreshDatablockList());
		this.refreshSubject.next();
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}
	public onSelectionChange($event: SelectionChangedEvent): void {
		this.toggleActions = $event.selectedRowsData.length > 0;
		this.selectionCount = $event.selectedRowsData.length;
	}

	public onOptionChange(e: OptionChangedEvent): void {
		if (!this.grid) {
			return;
		}
		const action: string = e.fullName;
		if (action === 'paging.pageIndex') {
			this.optionChangedSubject.next('page-change');
			return;
		}

		if (action === 'paging.pageSize') {
			this.optionChangedSubject.next('pageSize-change');
			return;
		}

		if (action.includes('filterValue')) {
			this.optionChangedSubject.next('filter');
			return;
		}

		if (action.includes('sortOrder')) {
			this.optionChangedSubject.next('sort');
			return;
		}
	}

	public async onGridContentReady(): Promise<void> {
		const loadTrigger = await firstValueFrom(this.loadTriggerSubject);

		if (loadTrigger) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.loadTriggerSubject.next(false);
						this.grid?.instance.endCustomLoading();
					})
				)
			);
		}
	}

	public rowMenuOpen($event: MouseEvent, rowIndex: number): void {
		$event.stopPropagation();
		// fake show menu button
		setTimeout(() => {
			this.currentlyHovered = rowIndex;
		}, 300);
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public createNewDatablock(): void {
		this.store.dispatch(navigateToDatablockCreate());
	}

	public stopPersistence(event: Event, datablock: DatablockItemEntity): void {
		event.stopPropagation();
		if (datablock.persistenceState.recentJobId === null) {
			return;
		}
		this.store.dispatch(
			stopPersistence({
				jobKey: datablock.persistenceState.recentJobId,
			})
		);
	}

	public openPersistencePopover(event: Event, id: number): void {
		event.stopPropagation();
		this.openedOverlayElement.persistence = id;
	}

	public closePersistencePopover(): void {
		this.openedOverlayElement.persistence = -1;
	}

	public openExpositionPopover(event: Event, id: number): void {
		event.stopPropagation();
		this.openedOverlayElement.exposition = id;
	}

	public closeExpositionPopover(): void {
		this.openedOverlayElement.exposition = -1;
	}

	public navigateToDatablock(id: number): void {
		this.store.dispatch(
			navigateToDatablock({
				id,
			})
		);
	}

	public createFromDatablock(event: Event, id: number): void {
		event.stopPropagation();
		this.store.dispatch(
			navigateToCreateFromDatablock({
				id,
			})
		);
	}

	public exportDatablock(event: Event, datablock: DatablockItemEntity): void {
		event.stopPropagation();
		this.store.dispatch(
			navigateToExportDatablock({
				id: datablock.id,
				label: datablock.label,
			})
		);
	}

	public duplicateDatablock(id: number): void {
		this.store.dispatch(
			navigateToDatablockDuplicate({
				id,
			})
		);
	}

	public deleteDatablockRow(
		event: Event,
		datablock: DatablockItemEntity
	): void {
		event.stopPropagation();
		this.openActionPopup(
			$localize`:i18n=@@datablock.action.popup.delete.header:`,
			[datablock.id],
			datablock.label,
			DatablockAction.Delete
		);
	}

	public persistDatablock(datablock: DatablockItemEntity): void {
		this.openActionPopup(
			$localize`:i18n=@@datablock.action.popup.persist.header:`,
			[datablock.id],
			datablock.label,
			DatablockAction.Persist
		);
	}

	public unpersistDatablock(datablock: DatablockItemEntity): void {
		this.openActionPopup(
			$localize`:i18n=@@datablock.action.popup.depersist.header:`,
			[datablock.id],
			datablock.label,
			DatablockAction.Depersist
		);
	}

	public repersistDatablock(datablock: DatablockItemEntity): void {
		this.openActionPopup(
			$localize`:i18n=@@datablock.action.popup.repersist.header:`,
			[datablock.id],
			datablock.label,
			DatablockAction.Repersist
		);
	}

	public toggleActivationStatus(datablock: DatablockItemEntity): void {
		this.store.dispatch(
			toggleDatablockStatus({
				datablockId: datablock.id,
			})
		);
	}

	public calculateTagsHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: DatablockItemEntity): boolean => {
			const tagsSet = column.calculateCellValue(
				data
			) as OrderedSet<AppTagItemEntity>;
			const tags = tagsSet.toList().toArray();
			if (tags.length === 0 && value === NoopValue) {
				return true;
			}

			return (
				!!tags &&
				!!tags.find((v) => checkStringEquality(v.label, value, filterOperation))
			);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculateActionByHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: DatablockItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as AvatarEntity;
			if (!entity) {
				return false;
			}
			const { name } = entity;
			return !!name && checkStringEquality(name, value, filterOperation);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculatePersistenceFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: DatablockItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as PersistenceState;
			if (entity === null && value === NoopValue) {
				return true;
			}
			if (!entity) {
				return false;
			}
			const { isPersisted } = entity;
			return isPersisted === !!value;
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	private async deleteSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedDatablocks = await firstValueFrom(
			this.datablockSelector.getSelectedDatablocks$(selectedRowKeys)
		);
		if (selectedDatablocks.length === 1) {
			return this.openActionPopup(
				$localize`:i18n=@@datablock.action.popup.delete.header:`,
				[selectedDatablocks[0].id],
				selectedDatablocks[0].label,
				DatablockAction.Delete
			);
		}
		return this.openActionPopup(
			$localize`:i18n=@@datablock.action.popup.delete.header:`,
			selectedDatablocks.map((k) => k.id),
			'',
			DatablockAction.Delete
		);
	}
	private async persistInDiscSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedDatablocks = await firstValueFrom(
			this.datablockSelector.getSelectedDatablocks$(selectedRowKeys)
		);
		if (selectedDatablocks.length === 1) {
			return this.openActionPopup(
				$localize`:i18n=@@datablock.action.popup.persist.header:`,
				[selectedDatablocks[0].id],
				selectedDatablocks[0].label,
				DatablockAction.Persist
			);
		}
		return this.openActionPopup(
			$localize`:i18n=@@datablock.action.popup.persist.header:`,
			selectedDatablocks.map((k) => k.id),
			'',
			DatablockAction.Persist
		);
	}
	private async unpersistSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedDatablocks = await firstValueFrom(
			this.datablockSelector.getSelectedDatablocks$(selectedRowKeys)
		);
		if (selectedDatablocks.length === 1) {
			return this.openActionPopup(
				$localize`:i18n=@@datablock.action.popup.depersist.header:`,
				[selectedDatablocks[0].id],
				selectedDatablocks[0].label,
				DatablockAction.Depersist
			);
		}
		return this.openActionPopup(
			$localize`:i18n=@@datablock.action.popup.depersist.header:`,
			selectedDatablocks.map((k) => k.id),
			'',
			DatablockAction.Depersist
		);
	}

	private openActionPopup(
		title: string,
		datablockId: Array<number>,
		element: string,
		action: DatablockAction
	): void {
		this.modalService.openPopup<IDatablockDeleteModalData>(
			DatablockActionPopupComponent,
			{
				title,
				subTitle: '',
				datablockId,
				element,
				action,
			},
			{
				disableClose: true,
			}
		);
	}

	private handleSubscriptions(): void {
		this.actions$
			.pipe(
				takeUntil(this.onDestroy$),
				ofType(
					deleteDatablockSuccess,
					bulkDeleteSuccess,
					deleteDatablockFail,
					bulkDeleteFail
				),
				tap(() => this.refresh())
			)
			.subscribe();
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [
			ActionIcons.ColumnExpositionChooser,
			ActionIcons.Delete,
			ActionIcons.Filter,
			ActionIcons.PersistInDisk,
			ActionIcons.UnPersist,
		];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.panelActions = [
			{
				order: 0,
				command: (): void => {
					this.grid?.instance.showColumnChooser();
				},
				iconName: actionMap.get(ActionIcons.ColumnExpositionChooser)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@datablock.list.columnSelection:`,
				typed: '',
			},
			{
				order: 1,
				command: (): Promise<void> => this.persistInDiscSelected(),
				iconName: actionMap.get(ActionIcons.PersistInDisk)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@datablock.list.diskPersistence:`,
				typed: 'plain-context-action',
			},
			{
				order: 2,
				command: (): Promise<void> => this.unpersistSelected(),
				iconName: actionMap.get(ActionIcons.UnPersist)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@datablock.list.action.depersistAll:`,
				typed: 'plain-context-action',
			},
			{
				order: 3,
				command: (): Promise<void> => this.deleteSelected(),
				iconName: actionMap.get(ActionIcons.Delete)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@datablock.list.deleteSelection:`,
				typed: 'plain-context-action',
			},
		];
	}
}
